/**
 * src/modules/orders-list/toolbar.ts — Faza 6.2.24 (2026-05-03) split.
 *
 * Master entry point `renderOrdersView()` + toolbar (sync, add, export CSV, search,
 * sort, view-mode toggle) + bulk bar (status, delete, print labels, select mode).
 * Dispatch między cards/table mode na podstawie `$ordersViewMode`.
 *
 * Search debounce 300ms, items per page = state.itemsPerPage (default 50 — perf).
 *
 * Wycięte z `src/modules/orders-list.ts` (1651 LOC) — patrz
 * `.plans/audit-orders-list-split.md`.
 */

import type { OrderRow, OrderStatusDef, IntegrationAccount } from '../../types/dto.ts';

// AUDYT 2026-08-11: po mutacji lista MUSI dostac SWIEZE dane — render ze starego
// store'u + cache strony (sessionStorage, TTL 60 s) pokazywal stan sprzed zmiany
// az do F5. Inwalidacja cache + wymuszony refetch (force omija juz sessionStorage).
function _odswiezListeZamowienPoMutacji() {
  try {
    var inv: any = (window as any)._invalidateOrdersPageCache;
    if (typeof inv === 'function') inv();
  } catch (_e) {}
  try {
    var ld: any = (window as any).loadAllegroOrdersIntoPanel
      || ((window as any).SellBaseApp || {}).loadAllegroOrdersIntoPanel;
    if (typeof ld === 'function') ld(true);
  } catch (_e) {}
}


import { $ordersViewMode } from '../../stores/orders.js';
import { _startPackingPoll } from '../packing.js';
import { withButtonSpinner } from '../../lib/button-spinner.ts';
import { _bindBulkActions } from './bulk-actions.ts';
import { syncOrdersSearchScopeUI } from '../orders-shared.ts';
import { showConfirm, showPrompt } from '../sb-dialogs.ts';
import { przelaczPanelArkusz } from '../_panel-sheet.ts';

/** P4: tekst z i18n z awaryjnym polskim (moduł czyta window.__sbI18n). */
function _tP4(key: string, fallback: string): string {
  var i18n = (window as any).__sbI18n;
  if (i18n && typeof i18n.t === 'function') {
    try { var out = i18n.t(key); if (out && out !== key) return String(out); } catch (_e) { /* noop */ }
  }
  return fallback;
}

var state: any = (typeof window !== 'undefined' && window.__sbState) || (function () {
  var _p = new Proxy({}, {
    get: function (_, k: any) { return window.__sbState ? (window.__sbState as any)[k] : undefined; },
    set: function (_, k: any, v: any) { if (window.__sbState) (window.__sbState as any)[k] = v; return true; },
    has: function (_, k: any) { return window.__sbState ? (k in (window.__sbState as any)) : false; },
  });
  return _p;
})();

// Lazy delegates (cross-module)
function _ensureStatusTreeMap(this: any, ...args: any[]) { return (((window.__sbExports && window.__sbExports._ensureStatusTreeMap) || window._ensureStatusTreeMap) as any).apply(this, args); }
function _refreshConnectedIntegrations(this: any, ...args: any[]) { return (((window.__sbExports && window.__sbExports._refreshConnectedIntegrations) || (window as any)._refreshConnectedIntegrations) as any).apply(this, args); }
function _renderOrdersStatusPanel(this: any, ...args: any[]) { return (((window.__sbExports && window.__sbExports._renderOrdersStatusPanel) || (window as any)._renderOrdersStatusPanel) as any).apply(this, args); }
function renderOrderCards(this: any, ...args: any[]) { return (((window.__sbExports && window.__sbExports.renderOrderCards) || (window as any).renderOrderCards) as any).apply(this, args); }
function renderOrdersTable(this: any, ...args: any[]) { return (((window.__sbExports && window.__sbExports.renderOrdersTable) || (window as any).renderOrdersTable) as any).apply(this, args); }
function loadAllegroOrdersIntoPanel(this: any, ...args: any[]) { return (((window.__sbExports && window.__sbExports.loadAllegroOrdersIntoPanel) || window.loadAllegroOrdersIntoPanel) as any).apply(this, args); }
function openNewOrderDrawer(this: any, ...args: any[]) { return (((window.__sbExports && window.__sbExports.openNewOrderDrawer) || window.openNewOrderDrawer) as any).apply(this, args); }
function getOrderApiId(this: any, ...args: any[]) { return (((window.__sbExports && window.__sbExports.getOrderApiId) || window.getOrderApiId) as any).apply(this, args); }
// statusColorForLabel/upsertOrderInStore przeniesione do bulk-actions.ts (2026-07-24)

/* -----------------------------------------------
     renderOrdersView — master entry point
     ----------------------------------------------- */

// Sprint 25 (2026-05-03): cross-marketplace stats fetcher z server endpoint.
// Eliminuje migotanie liczb w pasku statusów. Cache 60s server-side + client-side flag
// żeby unikać re-fetch przy wielokrotnym renderOrdersView() w tej samej sesji.
var _ordersStatsLastFetch = 0;
/**
 * @param wymuszone pomija throttle 60 s — po KAŻDEJ mutacji (status, grupa, tag) i przy
 *   cyklicznym odświeżaniu. Zgłoszenie usera 2026-08-21: pasek pokazywał „ZMONTOWANE 1",
 *   a lista po kliknięciu była pusta — bo lista dociągała się od razu, a liczniki
 *   siedziały na kliencie do minuty (i drugie tyle w cache serwera, który zrzucały
 *   wyłącznie przeniesienia do grup roboczych).
 */
async function _refreshOrdersServerStats(wymuszone?: boolean) {
    var now = Date.now();
    // Throttle 60s match z server cache TTL — jeśli niedawno fetched, skip.
    if (!wymuszone && now - _ordersStatsLastFetch < 60_000 && (state as any).ordersServerStats) return;
    _ordersStatsLastFetch = now;
    try {
      var stats = await SellBaseAPI.getOrdersStats('all');
      // S-91 (W2): odpowiedź „serwer liczy” nie może pomalować pasek zerami — dopytujemy za chwilę.
      if (stats && stats.computing === true) {
        _ordersStatsLastFetch = 0;
        setTimeout(function() { void _refreshOrdersServerStats(true); }, 1500);
        return;
      }
      if (stats && typeof stats.all === 'number') {
        (state as any).ordersServerStats = stats;
        // S-91: poprzedni wynik oddany, bo serwer właśnie liczy nowy — malujemy go i dopytujemy za chwilę.
        if (stats.stale === true) {
          _ordersStatsLastFetch = 0;
          setTimeout(function() { void _refreshOrdersServerStats(true); }, 2000);
        }
        // Re-render status panel z świeżymi liczbami (jeśli widoczny)
        if ((state as any).currentView === 'orders' && typeof _renderOrdersStatusPanel === 'function') {
          _renderOrdersStatusPanel();
        }
        // Sprint 25: powiadom inne komponenty (np. sidebar nav-badge) o świeżych statystykach
        try { window.dispatchEvent(new CustomEvent('orders:stats:updated', { detail: stats })); } catch (_) {}
      }
    } catch (e: any) {
      // Tolerancyjne — gdy endpoint błąd, fallback na lokalne licznik z SellBaseData.orders
      console.warn('[orders] getOrdersStats failed (fallback to local count):', e && e.message);
    }
}

// ── Cykliczne odświeżanie listy i liczników ─────────────────────────────────
// Zgłoszenie usera 2026-08-21: „zdarza się, że ktoś wejdzie w zamówienie i spakuje,
// a druga osoba również ma to zamówienie w realizacji, a ono jest już wysłane".
// Lista zamówień jest pracą WSPÓLNĄ — bez odpytywania serwera każdy widzi swój stan
// sprzed X minut. Wzorzec 1:1 z pollerem blokad pakowania (`_startPackingPoll`).
var ODSWIEZANIE_LISTY_MS = 30000;
var _ordersAutoTimer: any = null;
var _ordersAutoVisBound = false;

/** Ciche odświeżenie NIE MOŻE wyrwać roboty spod rąk. */
function _mozeCichoOdswiezyc(): boolean {
  try {
    if (typeof document !== 'undefined' && document.hidden) return false;
    if ((state as any).currentView !== 'orders') return false;
    // Trwa pobranie — drugie i tak by się rozminęło z tym w locie.
    if ((state as any).ordersHydrating) return false;
    // Zaznaczenie do akcji zbiorczej: przerysowanie listy zgubiłoby wybór, a to bywa
    // 200 pozycji wyklikanych pod konkretną akcję.
    var zazn = (state as any).ordersSelectedIds;
    if (zazn && typeof zazn.size === 'number' && zazn.size > 0) return false;
    // Otwarty modal = user jest w środku decyzji (np. wybór statusu dla paczki zamówień).
    if (document.querySelector('.modal-overlay, .modal-backdrop')) return false;
    // S-139 (audyt 07.10): odświeżenie w tle nie może zamknąć menu spod kursora — ani
    // dropdown statusu na karcie/wierszu, ani menu grup roboczych, ani trwającego
    // przeciągnięcia tagu na zamówienie (`.otag-drag` na chipie w trakcie dragstart→dragend).
    if (document.querySelector('.oc-status-dropdown.open')) return false;
    if (document.getElementById('ogrp-menu')) return false;
    if (document.querySelector('.otag-drag')) return false;
    // Pole edycji wewnątrz listy (np. wyszukiwarka per wiersz, jeśli kiedyś powstanie) —
    // przebudowa listy wyrwałaby je spod kursora/fokusu. Checkbox/radio (np. zaznaczenie
    // karty .order-card-check) WYŁĄCZONE z tego warunku (S-139 przegląd): to nie edycja
    // tekstu, a fokus na nim po kliknięciu i tak zostaje do następnego kliknięcia gdzie
    // indziej — bez wyjątku odświeżanie w tle stało do 30 s dłużej, niż powinno.
    var _aktywny = document.activeElement as HTMLElement | null;
    if (_aktywny && _aktywny.closest && _aktywny.closest('#orders-list-container') &&
        /^(INPUT|TEXTAREA|SELECT)$/.test(_aktywny.tagName) &&
        !(_aktywny.tagName === 'INPUT' && /^(checkbox|radio)$/i.test((_aktywny as HTMLInputElement).type || ''))) return false;
  } catch (_e) {
    return false;
  }
  return true;
}

async function _cichoOdswiezZamowienia(): Promise<void> {
  if (!_mozeCichoOdswiezyc()) return;
  // Liczniki NAJPIERW: `loadAllegroOrdersIntoPanel` kończy się renderem widoku, który
  // sam sięga po statystyki. Wymuszenie przed pobraniem listy stempluje throttle, więc
  // ten render skorzysta z tej samej odpowiedzi zamiast wysyłać drugie zapytanie.
  try { _refreshOrdersServerStats(true); } catch (_e) { /* liczniki nie blokują listy */ }
  try {
    // `true` = pomiń cache strony (te same dane co po ręcznym „Synchronizuj").
    await loadAllegroOrdersIntoPanel(true);
  } catch (_e) { /* następny tick spróbuje ponownie */ }
}

export function _startOrdersAutoRefresh() {
  if (_ordersAutoTimer) return;
  _ordersAutoTimer = setInterval(_cichoOdswiezZamowienia, ODSWIEZANIE_LISTY_MS);
  if (typeof document !== 'undefined' && !_ordersAutoVisBound) {
    _ordersAutoVisBound = true;
    // Powrót do karty po dłuższej przerwie to najgorszy moment na stary stan —
    // odświeżamy od razu, nie czekając na kolejny tick.
    document.addEventListener('visibilitychange', function() {
      if (document.visibilityState === 'visible') _cichoOdswiezZamowienia();
    });
  }
}

export function _stopOrdersAutoRefresh() {
  if (_ordersAutoTimer) {
    clearInterval(_ordersAutoTimer);
    _ordersAutoTimer = null;
  }
}

if (typeof window !== 'undefined') {
  var _wT: any = window as any;
  if (!_wT.__sbExports) _wT.__sbExports = {};
  // Wołane po mutacjach z INNYCH chunków (akcje zbiorcze, grupy robocze, tagi):
  // lista i pasek statusów muszą się rozjechać najwyżej na czas jednego requestu.
  _wT.__sbOdswiezStatsZamowien = function(wymuszone?: boolean) {
    try { return _refreshOrdersServerStats(wymuszone !== false); } catch (_e) { return null; }
  };
  _wT.__sbExports.odswiezStatsZamowien = _wT.__sbOdswiezStatsZamowien;
  // Zatrzymanie przy wyjściu z widoku i przy wylogowaniu — te same miejsca, w których
  // gaśnie poller blokad pakowania (legacy-router-utils.ts / legacy-navigation.ts).
  _wT._stopOrdersAutoRefresh = _stopOrdersAutoRefresh;
  _wT.__sbExports.stopOrdersAutoRefresh = _stopOrdersAutoRefresh;
}

// Słowniki panelu filtrów są tenant-scoped i lekkie. Ładujemy je raz na wejście
// w /orders; statusy nie są hardkodowane, więc własne statusy firmy pojawiają się
// automatycznie, a lista operatorów zawsze odpowiada zarejestrowanym użytkownikom.
var _ordersFilterOptionsPromise: Promise<any> | null = null;

function _selectedFilterValues(containerId: string): string[] {
  var out: string[] = [];
  document.querySelectorAll('#' + containerId + ' input[type="checkbox"]:checked').forEach(function(el: any) {
    var value = String(el.value || '').trim();
    if (value && out.indexOf(value) === -1) out.push(value);
  });
  return out;
}

var _ordersFilterLoadingSeq = 0;

function _setOrdersFilterLoading(active: boolean) {
  var panel = document.getElementById('orders-filters-panel');
  var list = document.getElementById('orders-list-container');
  var indicator = document.getElementById('orders-filter-loading-indicator');
  if (panel) {
    panel.classList.toggle('is-loading', active);
    panel.setAttribute('aria-busy', active ? 'true' : 'false');
  }
  if (list) list.classList.toggle('is-filter-loading', active);
  if (indicator) (indicator as HTMLElement).hidden = !active;
}

async function _loadOrdersWithFilterFeedback(button: HTMLElement | null = null) {
  var seq = ++_ordersFilterLoadingSeq;
  _setOrdersFilterLoading(true);
  // Pozwól przeglądarce namalować spinner i pasek postępu przed startem fetchu.
  await new Promise<void>(function(resolve) {
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(function() { resolve(); });
    else setTimeout(resolve, 0);
  });
  try {
    var runLoad = async function() {
      if (typeof loadAllegroOrdersIntoPanel === 'function') {
        await loadAllegroOrdersIntoPanel(true);
      } else {
        renderOrdersView();
      }
    };
    if (button) {
      await withButtonSpinner(
        button,
        {
          loadingText: 'Wyszukuję…',
          idleIcon: 'check',
          idleText: ' Zastosuj filtry',
          timeoutMs: 60_000,
          showErrorToast: false,
        },
        async function() { await runLoad(); },
      );
    } else {
      await runLoad();
    }
  } finally {
    // Starsza odpowiedź nie może zgasić loadera nowszego wyszukiwania.
    if (seq === _ordersFilterLoadingSeq) {
      _setOrdersFilterLoading(false);
      _pokazLiczbeWynikowFiltra();
    }
  }
}

// Licznik trafień (zgłoszenie usera 2026-09-01: „powinno też gdzieś wyświetlać
// ile zamówień znalazło dla danego filtra"). Serwerowy total ląduje w
// state.ordersTotalCount po każdym załadowaniu listy.
function _pokazLiczbeWynikowFiltra() {
  var el = document.getElementById('orders-filter-results-count');
  if (!el) return;
  var ile = Number((state as any).ordersTotalCount);
  if (!isFinite(ile) || ile < 0) { el.hidden = true; return; }
  var strong = el.querySelector('strong');
  var slowo = (ile === 1) ? 'zamówienie' : ((ile % 10 >= 2 && ile % 10 <= 4 && (ile % 100 < 12 || ile % 100 > 14)) ? 'zamówienia' : 'zamówień');
  if (strong) strong.textContent = 'Znaleziono ' + ile.toLocaleString('pl-PL') + ' ' + slowo;
  el.hidden = false;
  try { lucide.createIcons({ nodes: [el] }); } catch (_e) {}
}

function _hydrateOrdersAdvancedFilters() {
  var st = state as any;
  var topSearch = document.getElementById('order-search') as HTMLInputElement | null;
  var filterSearch = document.getElementById('orders-filter-search') as HTMLInputElement | null;
  var query = String(st.ordersFilterQuery || (topSearch && topSearch.value) || '');
  if (topSearch && topSearch.value !== query) topSearch.value = query;
  if (filterSearch && filterSearch.value !== query) filterSearch.value = query;

  var values: Record<string, any> = {
    'orders-filter-date-from': st.ordersFilterDateFrom || '',
    'orders-filter-date-to': st.ordersFilterDateTo || '',
    'orders-filter-status-changed-from': st.ordersFilterStatusChangedFrom || '',
    'orders-filter-status-changed-to': st.ordersFilterStatusChangedTo || '',
    'orders-filter-amount-min': st.ordersFilterAmountMinPln ?? '',
    'orders-filter-amount-max': st.ordersFilterAmountMaxPln ?? '',
    'orders-filter-document-state': st.ordersFilterDocumentState || '',
    'orders-filter-buyer-type': st.ordersFilterBuyerType || '',
    'orders-filter-sent-by': st.ordersFilterSentByUserId || '',
    'orders-filter-sku-ean': st.ordersFilterSkuEan || '',
    'orders-filter-pickup-point': st.ordersFilterPickupPoint || '',
  };
  Object.keys(values).forEach(function(id) {
    var el = document.getElementById(id) as HTMLInputElement | HTMLSelectElement | null;
    if (el) el.value = String(values[id]);
  });

  var statuses: string[] = Array.isArray(st.ordersFilterStatuses) ? st.ordersFilterStatuses : [];
  document.querySelectorAll('#orders-filter-statuses input[type="checkbox"]').forEach(function(el: any) {
    el.checked = statuses.indexOf(String(el.value)) !== -1;
  });
  var sources: string[] = Array.isArray(st.ordersFilterSources) ? st.ordersFilterSources : [];
  document.querySelectorAll('#orders-filter-sources input[type="checkbox"]').forEach(function(el: any) {
    el.checked = sources.indexOf(String(el.value)) !== -1;
  });
  var currencies: string[] = Array.isArray(st.ordersFilterCurrencies) ? st.ordersFilterCurrencies : [];
  document.querySelectorAll('#orders-filter-currencies input[type="checkbox"]').forEach(function(el: any) {
    // Pusty stan oznacza „wszystkie waluty", dlatego wszystkie chipy są aktywne.
    el.checked = currencies.length === 0 || currencies.indexOf(String(el.value)) !== -1;
  });
}

async function _loadOrdersFilterOptions() {
  if (_ordersFilterOptionsPromise) return _ordersFilterOptionsPromise;
  _ordersFilterOptionsPromise = (async function() {
    var statusHost = document.getElementById('orders-filter-statuses');
    var userSelect = document.getElementById('orders-filter-sent-by') as HTMLSelectElement | null;
    try {
      var data = await SellBaseAPI.getOrderFilterOptions();
      var statuses = data && Array.isArray(data.statuses) ? data.statuses : [];
      if (statusHost) {
        var statusHostEl = statusHost;
        statusHostEl.innerHTML = '';
        if (!statuses.length) {
          statusHostEl.innerHTML = '<span class="orders-filter-hint">Brak skonfigurowanych statusów.</span>';
        } else {
          statuses.forEach(function(s: any) {
            var label = document.createElement('label');
            label.className = 'orders-filter-chip orders-filter-status-chip';
            var color = String(s.color || '').trim();
            label.style.setProperty('--chip-color', /^#[0-9a-f]{3,8}$/i.test(color) ? color : 'var(--color-primary)');
            var input = document.createElement('input');
            input.type = 'checkbox';
            input.value = String(s.key || '');
            var iconWrap = document.createElement('span');
            iconWrap.className = 'orders-filter-chip-icon';
            var iconName = String(s.icon || '').trim().toLowerCase();
            if (/^[a-z0-9-]+$/.test(iconName)) {
              var icon = document.createElement('i');
              icon.setAttribute('data-lucide', iconName);
              iconWrap.appendChild(icon);
            }
            var span = document.createElement('span');
            span.textContent = String(s.name || s.key || 'Status');
            label.appendChild(input);
            label.appendChild(iconWrap);
            label.appendChild(span);
            statusHostEl.appendChild(label);
          });
          try { lucide.createIcons({ nodes: [statusHostEl] }); } catch (_) {}
        }
      }
      if (userSelect) {
        var userSelectEl = userSelect;
        userSelectEl.innerHTML = '<option value="">Każdy użytkownik / automat</option>';
        var users = data && Array.isArray(data.users) ? data.users : [];
        users.forEach(function(u: any) {
          var option = document.createElement('option');
          option.value = String(u.id || '');
          var name = String(u.name || u.email || ('Użytkownik #' + u.id));
          var email = String(u.email || '');
          option.textContent = email && email.toLowerCase() !== name.toLowerCase()
            ? name + ' — ' + email
            : name;
          userSelectEl.appendChild(option);
        });
      }
      _hydrateOrdersAdvancedFilters();
      return data;
    } catch (e: any) {
      _ordersFilterOptionsPromise = null;
      if (statusHost) statusHost.innerHTML = '<span class="orders-filter-hint" style="color:var(--color-error)">Nie udało się pobrać statusów. Zamknij i otwórz filtry ponownie.</span>';
      console.warn('[orders/filters] options failed:', e && e.message);
      return null;
    }
  })();
  return _ordersFilterOptionsPromise;
}

export function renderOrdersView() {
    // Załaduj mapę statusów z drzewka (lazy)
    _ensureStatusTreeMap();
    // Start packing lock polling
    _startPackingPoll();

    // Lazy fetch connected integrations (Allegro + PS accounts) — pokaż w filtrze Źródło
    _refreshConnectedIntegrations();

    // Sprint 25: server stats refresh (background, non-blocking).
    // Pasek statusów pokaże pełne liczby w < 100ms zamiast czekać na stage 2 prefetch.
    _refreshOrdersServerStats();

    // Cykliczne odświeżanie listy — idempotentne, pilnuje się samo (patrz wyżej).
    _startOrdersAutoRefresh();

    // Render status panel
    _renderOrdersStatusPanel();
    syncOrdersSearchScopeUI();

    // Render list based on view mode
    // S-160 (MOB-2): na telefonie zawsze karty — tabela ma ~650 px i poziomy scroll, a przełącznik jest schowany (mobile.css).
    if ($ordersViewMode.get() === 'cards' || _telefonWidok()) {
      renderOrderCards();
    } else {
      renderOrdersTable();
    }

    // Update toggle buttons
    var toggleBtns = document.querySelectorAll('.orders-toggle-btn');
    toggleBtns.forEach(function(btn) {
      btn.classList.toggle('active', (btn as any).dataset.view === $ordersViewMode.get());
    });

    // Bind toolbar events (once)
    _bindOrdersToolbarEvents();

    // 2026-07-24 (grupy wysyłkowe): badge „⧉ grupa #G-x" na kartach/wierszach —
    // debounce w środku, patch DOM po dociągnięciu danych listy.
    try { ((window as any)._refreshGroupBadges || function () {})(); } catch (_) {}
}

function _telefonWidok(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 768px)').matches;
}

export function _bindOrdersToolbarEvents() {
    // Panel toggle
    var panelToggle = document.getElementById('orders-panel-toggle-btn');
    if (panelToggle && !((panelToggle as any)._bound)) {
      (panelToggle as any)._bound = true;
      panelToggle.addEventListener('click', function(this: any) {
        // S-160: na ≤1024 px panel statusów jest ukryty w CSS — pokazujemy go jako wysuwany arkusz.
        if (przelaczPanelArkusz(document.getElementById('orders-status-panel'))) return;
        (state as any).ordersPanelVisible = !((state as any).ordersPanelVisible);
        var panel = document.getElementById('orders-status-panel');
        var layout = document.querySelector('.orders-layout');
        if (panel) panel.style.display = (state as any).ordersPanelVisible ? '' : 'none';
        // 2026-07-19 (audyt RWD #4): klasa zamiast inline gridTemplateColumns — inline wygrywał
        // z media query ≤1024px i zostawiał martwą kolumnę 200px po zwężeniu okna.
        if (layout) layout.classList.toggle('panel-hidden', !((state as any).ordersPanelVisible));
      });
    }

    var syncBtn = document.getElementById('orders-sync-btn');
    if (syncBtn && !((syncBtn as any)._bound)) {
      (syncBtn as any)._bound = true;
      syncBtn.addEventListener('click', async function(this: any) {
        // 2026-05-20 v062: BaseLinker/Sellasist-style instant sync.
        // Strategy: background worker `allegro-sync-worker` (interval=60s) sam ciągnie nowe eventy.
        // Button tylko: (1) FORCE worker tick teraz (GET /api/allegro/orders/sync — zwraca natychmiast,
        // backend uruchamia thread), (2) refresh listy z DB po 1-2s żeby pokazać świeże dane jeśli
        // worker już wrzucił. Bez czekania na completion.
        // Shipments sync: ZUPEŁNIE usunięty z buttona — background worker `shipping-tracking-worker`
        // (interval=600s) i `shipping-stale-watchdog` (3600s) i tak ciągną waybille.
        if ((syncBtn as any)._inProgress) return;
        (syncBtn as any)._inProgress = true;
        (syncBtn as any).disabled = true;
        (syncBtn as any).innerHTML = '<i data-lucide="loader-2" style="width:14px;height:14px;animation:spin 1s linear infinite"></i> Sprawdzam...';
        lucide.createIcons({ nodes: [syncBtn as HTMLElement] });

        var ordersBefore = ((window as any).SellBaseData && (window as any).SellBaseData.orders && (window as any).SellBaseData.orders.length) || 0;

        try {
          // (1) Force trigger Allegro sync workers — per każde aktywne konto. Każdy call zwraca natychmiast
          // (background thread w api_server). Errors swallowed — jeśli endpoint padnie, refresh i tak działa.
          var allegroAccounts = ((window as any).__sbState && (window as any).__sbState.connectedIntegrations
            && (window as any).__sbState.connectedIntegrations.accounts
            && (window as any).__sbState.connectedIntegrations.accounts.allegro) || [];
          if (allegroAccounts.length > 0 && (window as any).SellBaseAPI && (window as any).SellBaseAPI.syncOrders) {
            // fire-and-forget per account
            allegroAccounts.forEach(function(acc: IntegrationAccount) {
              try { (window as any).SellBaseAPI.syncOrders(acc.id, { mode: 'events', limit: 200 }).catch(function() {}); } catch (_) {}
            });
          } else if ((window as any).SellBaseAPI && (window as any).SellBaseAPI.syncOrders) {
            // Fallback: bez konkretnego account_id — backend bierze default
            try { (window as any).SellBaseAPI.syncOrders(null, { mode: 'events', limit: 200 }).catch(function() {}); } catch (_) {}
          }

          // (2) Krótki delay (1.5s) żeby dać workerom czas wrzucić nowe BOUGHT events do DB.
          // Worker pobiera batch ~200 events w ~1s; SQL inserts ~few hundred ms.
          await new Promise(function(r: any) { setTimeout(r, 1500); });

          // (3) Invalidate cache + refresh listy
          if (typeof (window as any)._invalidateOrdersPageCache === 'function') {
            (window as any)._invalidateOrdersPageCache();
          }
          await loadAllegroOrdersIntoPanel(true);

          var ordersAfter = ((window as any).SellBaseData && (window as any).SellBaseData.orders && (window as any).SellBaseData.orders.length) || 0;
          var delta = Math.max(0, ordersAfter - ordersBefore);
          if (delta > 0) {
            showToast('Pobrano ' + delta + ' nowych zamówień', 'success');
          } else {
            showToast('Brak nowych zamówień. Lista odświeżona.', 'success');
          }
          try { renderOrdersView(); } catch (_) {}
          // Broadcast cross-tab
          try {
            if (typeof BroadcastChannel !== 'undefined') {
              var bc = new BroadcastChannel('sellbase-orders');
              bc.postMessage({ type: 'orders:updated', source: 'sync-btn' });
              bc.close();
            }
          } catch (_) {}
        } catch (e: any) {
          try { showToast('Błąd: ' + (e.message || e), 'error'); } catch (_) {}
        } finally {
          (syncBtn as any)._inProgress = false;
          (syncBtn as any).disabled = false;
          (syncBtn as any).innerHTML = '<i data-lucide="refresh-cw" style="width:14px;height:14px"></i> Synchronizuj';
          try { lucide.createIcons({ nodes: [syncBtn as HTMLElement] }); } catch (_) {}
        }
      });
    }

    var addBtn = document.getElementById('orders-add-btn');
    if (addBtn && !((addBtn as any)._bound)) {
      (addBtn as any)._bound = true;
      addBtn.addEventListener('click', async function(this: any) {
        // Fix (2026-05-28): chunk new-order-drawer.ts jest lazy-loadowany tylko przez
        // route /orders/new. Na /orders window.openNewOrderDrawer = undefined → klik
        // nic nie robił. Lazy-import chunka przed wywołaniem (wzór jak offers assign-product).
        if (typeof (window as any).openNewOrderDrawer !== 'function') {
          (this as HTMLButtonElement).disabled = true;
          try {
            await import('../new-order-drawer.ts');
          } catch (e: any) {
            try { showToast('Nie udało się załadować formularza: ' + (e && e.message || e), 'error'); } catch (_) {}
            (this as HTMLButtonElement).disabled = false;
            return;
          }
          (this as HTMLButtonElement).disabled = false;
        }
        try { openNewOrderDrawer(); } catch (e: any) {
          try { showToast('Błąd otwierania formularza: ' + (e && e.message || e), 'error'); } catch (_) {}
        }
      });
    }

    // Sprint 20.11+ (2026-05-20): main toolbar "Eksport CSV" usunięty — eksport tylko via bulk bar
    // (#bulk-export-selected). Logika eksportu masowego z server-side filtrami została przeniesiona/usunięta.

    // Rozszerzone filtry zamówień: cały panel działa server-side po pełnej bazie,
    // nie po 50 wierszach aktualnie widocznej strony.
    var filtersToggleBtn = document.getElementById('orders-filters-toggle-btn');
    if (filtersToggleBtn && !((filtersToggleBtn as any)._bound)) {
      (filtersToggleBtn as any)._bound = true;
      // 2026-07-24 (kosmetyka, życzenie usera): otwarty panel filtrów = przycisk
      // w kolorze primary (jak aktywny przełącznik widoku karty/tabela).
      var _paintFiltersBtn = function(open: boolean) {
        var b: any = filtersToggleBtn;
        if (open) {
          b.style.background = 'var(--color-primary)';
          b.style.borderColor = 'var(--color-primary)';
          b.style.color = '#fff';
        } else {
          b.style.background = '';
          b.style.borderColor = '';
          b.style.color = '';
        }
      };
      // Restore persisted state (open/closed)
      try {
        var savedOpen = localStorage.getItem('sellbase_orders_filters_open') === '1';
        var panelInit = document.getElementById('orders-filters-panel');
        if (panelInit) panelInit.style.display = savedOpen ? '' : 'none';
        _paintFiltersBtn(savedOpen);
        if (savedOpen) {
          _hydrateOrdersAdvancedFilters();
          void _loadOrdersFilterOptions();
        }
      } catch (_) {}
      filtersToggleBtn.addEventListener('click', function(this: any) {
        var panel = document.getElementById('orders-filters-panel');
        if (!panel) return;
        var nowOpen = panel.style.display === 'none';
        panel.style.display = nowOpen ? '' : 'none';
        try { localStorage.setItem('sellbase_orders_filters_open', nowOpen ? '1' : '0'); } catch (_) {}
        _paintFiltersBtn(nowOpen);
        if (nowOpen) {
          _hydrateOrdersAdvancedFilters();
          void _loadOrdersFilterOptions();
        }
        try { lucide.createIcons({ nodes: [filtersToggleBtn as HTMLElement] }); } catch (_) {}
      });
    }

    // Klik w dowolną część pola daty otwiera natywny kalendarz (Chrome/Safari).
    // 2026-09-01: pola „Zmiana statusu od/do" doszły bez tego wpisu — kalendarz
    // otwierał się tylko z małej ikonki, co user zgłosił jako „nie pojawia się".
    ['orders-filter-date-from', 'orders-filter-date-to',
     'orders-filter-status-changed-from', 'orders-filter-status-changed-to'].forEach(function(id) {
      var dateEl = document.getElementById(id) as HTMLInputElement | null;
      if (dateEl && !((dateEl as any)._pickerBound)) {
        (dateEl as any)._pickerBound = true;
        dateEl.addEventListener('click', function() {
          try { if (typeof (dateEl as any).showPicker === 'function') (dateEl as any).showPicker(); } catch (_) {}
        });
      }
    });

    function _updateFiltersActiveBadge() {
      var st = state as any;
      var count = 0;
      if (String(st.ordersFilterQuery || '').trim()) count++;
      if (st.ordersFilterDateFrom) count++;
      if (st.ordersFilterDateTo) count++;
      if (st.ordersFilterAmountMinPln != null && st.ordersFilterAmountMinPln !== '') count++;
      if (st.ordersFilterAmountMaxPln != null && st.ordersFilterAmountMaxPln !== '') count++;
      var curs: string[] = Array.isArray(st.ordersFilterCurrencies) ? st.ordersFilterCurrencies : [];
      if (curs.length && curs.length < 5) count++;
      if (Array.isArray(st.ordersFilterStatuses) && st.ordersFilterStatuses.length) count++;
      if (Array.isArray(st.ordersFilterSources) && st.ordersFilterSources.length) count++;
      if (st.ordersFilterDocumentState) count++;
      if (st.ordersFilterPokrycie) count++;  // S-91 (D5): „Do sprawdzenia” z karty automatu dokumentów
      if (st.ordersFilterBuyerType) count++;
      if (st.ordersFilterSentByUserId) count++;
      if (st.ordersFilterStatusChangedFrom) count++;
      if (st.ordersFilterStatusChangedTo) count++;
      if (st.ordersFilterSkuEan) count++;
      if (st.ordersFilterPickupPoint) count++;
      var badge = document.getElementById('orders-filters-active-count');
      if (badge) {
        if (count > 0) { badge.textContent = String(count); badge.style.display = ''; }
        else { badge.style.display = 'none'; }
      }
    }

    var filtersApplyBtn = document.getElementById('orders-filters-apply');
    if (filtersApplyBtn && !((filtersApplyBtn as any)._bound)) {
      (filtersApplyBtn as any)._bound = true;
      filtersApplyBtn.addEventListener('click', async function(this: any) {
        var st = state as any;
        var filterSearch = document.getElementById('orders-filter-search') as HTMLInputElement | null;
        var topSearch = document.getElementById('order-search') as HTMLInputElement | null;
        var dfEl = document.getElementById('orders-filter-date-from') as HTMLInputElement | null;
        var dtEl = document.getElementById('orders-filter-date-to') as HTMLInputElement | null;
        var amnEl = document.getElementById('orders-filter-amount-min') as HTMLInputElement | null;
        var amxEl = document.getElementById('orders-filter-amount-max') as HTMLInputElement | null;
        var docEl = document.getElementById('orders-filter-document-state') as HTMLSelectElement | null;
        var buyerEl = document.getElementById('orders-filter-buyer-type') as HTMLSelectElement | null;
        var sentEl = document.getElementById('orders-filter-sent-by') as HTMLSelectElement | null;
        st.ordersFilterQuery = String((filterSearch && filterSearch.value) || '').trim();
        if (topSearch) topSearch.value = st.ordersFilterQuery;
        st.ordersSearchActive = st.ordersFilterQuery.length >= 2;
        syncOrdersSearchScopeUI();
        st.ordersFilterDateFrom = dfEl && dfEl.value ? dfEl.value : '';
        st.ordersFilterDateTo = dtEl && dtEl.value ? dtEl.value : '';
        var scfEl = document.getElementById('orders-filter-status-changed-from') as HTMLInputElement | null;
        var sctEl = document.getElementById('orders-filter-status-changed-to') as HTMLInputElement | null;
        st.ordersFilterStatusChangedFrom = scfEl && scfEl.value ? scfEl.value : '';
        st.ordersFilterStatusChangedTo = sctEl && sctEl.value ? sctEl.value : '';
        st.ordersFilterAmountMinPln = amnEl && amnEl.value !== '' ? amnEl.value : '';
        st.ordersFilterAmountMaxPln = amxEl && amxEl.value !== '' ? amxEl.value : '';
        var checkedCurrencies = _selectedFilterValues('orders-filter-currencies');
        st.ordersFilterCurrencies = checkedCurrencies.length === 5 ? [] : checkedCurrencies;
        st.ordersFilterStatuses = _selectedFilterValues('orders-filter-statuses');
        st.ordersFilterSources = _selectedFilterValues('orders-filter-sources');
        st.ordersFilterDocumentState = docEl ? docEl.value : '';
        st.ordersFilterBuyerType = buyerEl ? buyerEl.value : '';
        st.ordersFilterSentByUserId = sentEl ? sentEl.value : '';
        // 2026-09-02 (user): filtr SKU / EAN produktu.
        var skuEl = document.getElementById('orders-filter-sku-ean') as HTMLInputElement | null;
        st.ordersFilterSkuEan = skuEl ? String(skuEl.value || '').trim() : '';
        // 2026-09-11 (właściciel): nr paczkomatu / punktu odbioru odbiorcy.
        var punktEl = document.getElementById('orders-filter-pickup-point') as HTMLInputElement | null;
        st.ordersFilterPickupPoint = punktEl ? String(punktEl.value || '').trim().toUpperCase() : '';
        // Rozszerzony wybór jest nadrzędny wobec pojedynczego chipa w lewym panelu.
        if (st.ordersFilterStatuses.length) st.ordersStatusFilter = 'all';
        if (st.ordersFilterSources.length) {
          st.ordersSourceFilter = 'all';
          st.ordersAccountFilter = null;
        }
        st.currentPage = st.currentPage || {};
        st.currentPage.orders = 1;
        _updateFiltersActiveBadge();
        await _loadOrdersWithFilterFeedback(this as HTMLElement);
      });
    }

    var filtersClearBtn = document.getElementById('orders-filters-clear');
    if (filtersClearBtn && !((filtersClearBtn as any)._bound)) {
      (filtersClearBtn as any)._bound = true;
      filtersClearBtn.addEventListener('click', async function(this: any) {
        var st = state as any;
        var filterSearch = document.getElementById('orders-filter-search') as HTMLInputElement | null;
        var topSearch = document.getElementById('order-search') as HTMLInputElement | null;
        var dfEl = document.getElementById('orders-filter-date-from') as HTMLInputElement | null;
        var dtEl = document.getElementById('orders-filter-date-to') as HTMLInputElement | null;
        var amnEl = document.getElementById('orders-filter-amount-min') as HTMLInputElement | null;
        var amxEl = document.getElementById('orders-filter-amount-max') as HTMLInputElement | null;
        if (filterSearch) filterSearch.value = '';
        if (topSearch) topSearch.value = '';
        if (dfEl) dfEl.value = '';
        if (dtEl) dtEl.value = '';
        if (amnEl) amnEl.value = '';
        if (amxEl) amxEl.value = '';
        ['orders-filter-status-changed-from', 'orders-filter-status-changed-to'].forEach(function(id) {
          var el = document.getElementById(id) as HTMLInputElement | null;
          if (el) el.value = '';
        });
        document.querySelectorAll('#orders-filters-panel input[type="checkbox"]').forEach(function(b: any) {
          b.checked = b.closest('#orders-filter-currencies') !== null;
        });
        ['orders-filter-document-state', 'orders-filter-buyer-type', 'orders-filter-sent-by'].forEach(function(id) {
          var el = document.getElementById(id) as HTMLSelectElement | null;
          if (el) el.value = '';
        });
        st.ordersFilterQuery = '';
        st.ordersFilterDateFrom = '';
        st.ordersFilterDateTo = '';
        st.ordersFilterAmountMinPln = '';
        st.ordersFilterAmountMaxPln = '';
        st.ordersFilterCurrencies = [];
        st.ordersFilterStatuses = [];
        st.ordersFilterSources = [];
        st.ordersFilterDocumentState = '';
        st.ordersFilterPokrycie = '';
        st.ordersFilterBuyerType = '';
        st.ordersFilterSentByUserId = '';
        st.ordersFilterStatusChangedFrom = '';
        st.ordersFilterStatusChangedTo = '';
        st.ordersFilterSkuEan = '';
        st.ordersFilterPickupPoint = '';
        st.ordersSearchActive = false;
        syncOrdersSearchScopeUI();
        st.currentPage = st.currentPage || {};
        st.currentPage.orders = 1;
        _updateFiltersActiveBadge();
        await _loadOrdersWithFilterFeedback(null);
      });
    }

    // Update badge on init (po hydrate stanu z legacy bridge / localStorage)
    _updateFiltersActiveBadge();

    var searchEl = document.getElementById('order-search');
    if (searchEl && !((searchEl as any)._bound)) {
      (searchEl as any)._bound = true;
      var _orderSearchDebounce: any;
      searchEl.addEventListener('input', function(this: any) {
        clearTimeout(_orderSearchDebounce);
        var qNow = String(((searchEl as any).value || '')).trim();
        (state as any).ordersSearchInputRevision =
          Number((state as any).ordersSearchInputRevision || 0) + 1;
        // Stan zmieniamy NATYCHMIAST, nie dopiero po debounce/odpowiedzi API.
        // Inaczej przez 300 ms stary filtr grupy odrzucał prawidłowe wyniki.
        (state as any).ordersFilterQuery = qNow;
        (state as any).ordersSearchActive = qNow.length >= 2;
        (state as any).currentPage = (state as any).currentPage || {};
        (state as any).currentPage.orders = 1;
        syncOrdersSearchScopeUI();
        var filterSearch = document.getElementById('orders-filter-search') as HTMLInputElement | null;
        if (filterSearch) filterSearch.value = String((searchEl as HTMLInputElement).value || '');
        _orderSearchDebounce = setTimeout(async function() {
          var q = String(((searchEl as any).value || '')).trim();
          (state as any).ordersFilterQuery = q;
          (state as any).currentPage = (state as any).currentPage || {};
          (state as any).currentPage.orders = 1;
          _updateFiltersActiveBadge();
          // Backend uruchamia wyszukiwanie od 2 znaków; przy 0/1 znaku pobiera
          // zwykłą listę. Każda zmiana robi nowy request z ochroną przed race.
          (state as any).ordersSearchActive = q.length >= 2;
          syncOrdersSearchScopeUI();
          await _loadOrdersWithFilterFeedback(null);
        }, 300);
      });
    }

    var advancedSearch = document.getElementById('orders-filter-search') as HTMLInputElement | null;
    if (advancedSearch && !((advancedSearch as any)._bound)) {
      var advancedSearchEl = advancedSearch;
      (advancedSearch as any)._bound = true;
      advancedSearch.addEventListener('input', function() {
        var top = document.getElementById('order-search') as HTMLInputElement | null;
        if (top) top.value = advancedSearchEl.value;
      });
      advancedSearch.addEventListener('keydown', function(e: KeyboardEvent) {
        if (e.key === 'Enter') {
          e.preventDefault();
          (document.getElementById('orders-filters-apply') as HTMLButtonElement | null)?.click();
        }
      });
    }

    // 2026-09-02: Enter w polu SKU / EAN = Zastosuj (jak w polu wyszukiwania panelu).
    // 2026-09-11: Enter w polu nr paczkomatu = Zastosuj (jak w polu SKU / EAN).
    var punktFilterEl = document.getElementById('orders-filter-pickup-point') as HTMLInputElement | null;
    if (punktFilterEl && !((punktFilterEl as any)._bound)) {
      (punktFilterEl as any)._bound = true;
      punktFilterEl.addEventListener('keydown', function(e: KeyboardEvent) {
        if (e.key === 'Enter') {
          e.preventDefault();
          (document.getElementById('orders-filters-apply') as HTMLButtonElement | null)?.click();
        }
      });
    }
    var skuFilterEl = document.getElementById('orders-filter-sku-ean') as HTMLInputElement | null;
    if (skuFilterEl && !((skuFilterEl as any)._bound)) {
      (skuFilterEl as any)._bound = true;
      skuFilterEl.addEventListener('keydown', function(e: KeyboardEvent) {
        if (e.key === 'Enter') {
          e.preventDefault();
          (document.getElementById('orders-filters-apply') as HTMLButtonElement | null)?.click();
        }
      });
    }

    var sortSel = document.getElementById('order-sort-select');
    // 2026-09-03 (user): po odświeżeniu kontrolka wracała na „Najnowsze" — stan startowy
    // niesie zapamiętany tryb (legacy-state), tu tylko odbijamy go w <select>.
    if (sortSel && (state as any).ordersSortMode && (sortSel as any).value !== (state as any).ordersSortMode) {
      (sortSel as any).value = (state as any).ordersSortMode;
    }
    if (sortSel && !((sortSel as any)._bound)) {
      (sortSel as any)._bound = true;
      sortSel.addEventListener('change', function(this: any) {
        (state as any).currentPage.orders = 1;
        // Kolejność po dacie rozstrzyga BAZA — samo przerysowanie odwróciłoby tylko
        // 50 wierszy bieżącej strony (patrz komentarz przy `params.sort`).
        (state as any).ordersSortMode = String(this.value || 'date_desc');
        try { localStorage.setItem('sellbase_orders_sort', (state as any).ordersSortMode); } catch (_e) { /* prywatny tryb */ }
        if (String(this.value || '').indexOf('date_') === 0) {
          try { (window as any)._invalidateOrdersPageCache && (window as any)._invalidateOrdersPageCache(); } catch (_e) { /* cache to optymalizacja */ }
          loadAllegroOrdersIntoPanel(true);
          return;
        }
        renderOrdersView();
      });
    }

    var toggleBtns = document.querySelectorAll('.orders-toggle-btn');
    toggleBtns.forEach(function(btn) {
      if (!((btn as any)._bound)) {
        (btn as any)._bound = true;
        btn.addEventListener('click', function(this: any) {
          $ordersViewMode.set(this.dataset.view);
          // Clear injected content
          var lc = document.getElementById('orders-list-container');
          if (lc) lc.innerHTML = '';
          renderOrdersView();
        });
      }
    });

    // Bulk bar — akcje masowe wydzielone do bulk-actions.ts (2026-07-24):
    // zmiana statusu (współbieżność + raport per zamówienie), notatka, wiadomość,
    // łączenie zamówień, etykiety. Bind idempotentny (guard _bound wewnątrz).
    _bindBulkActions();

    var bulkDesel = document.getElementById('bulk-deselect');
    if (bulkDesel && !((bulkDesel as any)._bound)) {
      (bulkDesel as any)._bound = true;
      bulkDesel.addEventListener('click', function(this: any) {
        // „Odznacz" czysci ZAZNACZENIE, ale nie chowa juz checkboxow — tryb wybierania
        // przestal istniec (2026-08-10).
        (state as any).ordersSelectedIds.clear();
        _updateBulkBar();
        _updateSelectModeUI();
        renderOrdersView();
      });
    }

    // Sprint 20.10 (2026-05-02): bulk export selected to CSV
    var bulkExport = document.getElementById('bulk-export-selected');
    if (bulkExport && (!(bulkExport as any)._bound)) {
      (bulkExport as any)._bound = true;
      bulkExport.addEventListener('click', async function() {
        var ids = Array.from((state as any).ordersSelectedIds) as string[];
        if (!ids.length) { showToast('Nie zaznaczono żadnego zamówienia', 'warning'); return; }
        // Buduj CSV lokalnie z store (już mamy dane w pamięci)
        var rows: any[] = ids.map(function(id) { return SellBaseData.getOrderById(id); }).filter(Boolean);
        if (!rows.length) { showToast('Nie znaleziono danych', 'error'); return; }
        var lines = ['id,source,status,payment_status,bought_at,buyer_login,buyer_email,total_amount,currency'];
        rows.forEach(function(o: any) {
          var row = [
            JSON.stringify(o.id || ''),
            // 2026-06-10 BUGFIX: o.source bywa obiektem {key,label,color} — JSON.stringify
            // calego obiektu rozjezdzal kolumny CSV. Bierzemy sam key (jak status bierze label).
            JSON.stringify((o.source && o.source.key) || o.source || ''),
            JSON.stringify((o.status && o.status.label) || o.status || ''),
            JSON.stringify(o.payment_status || ''),
            JSON.stringify(o.date || o.bought_at || ''),
            JSON.stringify(o.buyer_login || ''),
            JSON.stringify(o.buyer_email || o.customerEmail || ''),
            JSON.stringify(o.total_amount || o.amount || ''),
            JSON.stringify(o.currency || 'PLN'),
          ].join(',');
          lines.push(row);
        });
        var csv = lines.join('\n');
        var blob = new Blob(['﻿' + csv], { type: 'text/csv; charset=utf-8' });
        var url = URL.createObjectURL(blob);
        var a = document.createElement('a');
        a.href = url;
        a.download = 'orders-selected-' + new Date().toISOString().slice(0, 10) + '.csv';
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
        showToast('Wyeksportowano ' + rows.length + ' zaznaczonych', 'success');
      });
    }

    // Sprint 20.10 (2026-05-02): bulk delete orders (master_admin only — backend sprawdza permissions)
    var bulkDelete = document.getElementById('bulk-delete-orders');
    if (bulkDelete && (!(bulkDelete as any)._bound)) {
      (bulkDelete as any)._bound = true;
      bulkDelete.addEventListener('click', async function() {
        var ids = Array.from((state as any).ordersSelectedIds) as string[];
        if (!ids.length) { showToast('Nie zaznaczono żadnego zamówienia', 'warning'); return; }
        // Confirmation 2-step (typowy dla destructive actions)
        var confirmed = await showConfirm(
          'Usunąć zaznaczone zamówienia?',
          'Zaznaczono: ' + ids.length + ' zamówień. Usuwamy rekord z bazy i powiązane order_events.',
          { okLabel: 'Usuń ' + ids.length + ' zamówień', danger: true, note: 'Operacja jest nieodwracalna.' }
        ).catch(function(){ return false; });
        if (!confirmed) return;

        // Sprint 20.10 SESSION 11: dla >10 ordersów wymuś re-wpisanie hasła
        if (ids.length > 10) {
          var pw = await showPrompt('Potwierdzenie hasłem', 'Usuwasz ' + ids.length + ' zamówień — wpisz swoje hasło.',
            { label: 'Hasło', type: 'password', required: true, danger: true, okLabel: 'Potwierdź i usuń' });
          if (!pw) { showToast('Anulowano usuwanie', 'info'); return; }
          try {
            var token = localStorage.getItem('orderflow_token')
              || sessionStorage.getItem('orderflow_token')
              || localStorage.getItem('sellbase_token') || '';
            var verifyResp = await fetch('/api/auth/verify-password', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
              body: JSON.stringify({ password: pw }),
            });
            if (!verifyResp.ok) {
              showToast('Hasło nieprawidłowe — operacja anulowana', 'error');
              return;
            }
          } catch (e) {
            showToast('Błąd weryfikacji hasła', 'error');
            return;
          }
        }

        await withButtonSpinner(bulkDelete as HTMLElement, { loadingText: 'Usuwam...', idleIcon: 'trash-2', idleText: ' Usuń', timeoutMs: 180_000, errorToastPrefix: 'Błąd usuwania: ' }, async (_signal) => {
          var done = 0, errors = 0, zDokumentami = 0;
          var apiIds: string[] = ids.map(function(id) {
            var o = SellBaseData.getOrderById(id); return o ? getOrderApiId(o) : null;
          }).filter(Boolean) as string[];

          // Throttle: po 5 jednoczesnych delete (nie wszystkie naraz)
          var batchSize = 5;
          for (var i = 0; i < apiIds.length; i += batchSize) {
            var batch = apiIds.slice(i, i + batchSize);
            await Promise.all(batch.map(async function(apiId) {
              try {
                await SellBaseAPI.deleteOrder(apiId);
                done++;
              } catch (e: any) {
                // P4/LC-8 (2026-09-15): zamówienie z dokumentem sprzedaży nie do usunięcia — liczone osobno.
                if (e && Number(e.status) === 409 && e.detail && e.detail.code === 'ORDER_HAS_DOCUMENTS') zDokumentami++;
                else errors++;
              }
            }));
          }

          // Refresh listy + cross-tab broadcast
          try { (window as any).__sbBroadcast?.broadcast('order:deleted', { count: done }); } catch (_) {}
          (state as any).ordersSelectedIds.clear();
          _updateBulkBar();
          _odswiezListeZamowienPoMutacji();

          if (zDokumentami) {
            showToast(_tP4('orders.bulk_delete_has_documents', '{n} zamówień ma dokumenty sprzedaży — nie usunięto').split('{n}').join(String(zDokumentami)), 'warning');
          }
          if (errors) {
            showToast('Usunięto ' + done + ' zamówień, ' + errors + ' błędów', 'warning');
          } else if (done || !zDokumentami) {
            showToast('Usunięto ' + done + ' zamówień', 'success');
          }
        });
      });
    }

    // „Drukuj etykiety” (#bulk-print-labels) — 23.10.2026 przeniesione do bulk-actions.ts
    // (_bulkPrintExistingLabels): druk przez SellBase Print zamiast window.open bez tokenu.

    // 2026-08-10: przycisk „Wybierz zamowienia" zniknal — checkboxy sa aktywne od razu,
    // wiec nie ma czego wlaczac. Jego miejsce w pasku przejmuje „Zaznacz wszystkie".

    // Sprint (2026-05-28): "Zaznacz wszystkie" — jeden klik włącza tryb wyboru
    // (jeśli wyłączony) I zaznacza WSZYSTKIE zamówienia na bieżącej stronie
    // (tyle ile per_page: 50/100/200). Toggle: jeśli już wszystkie zaznaczone → odznacz.
    var selectAllBtn = document.getElementById('orders-select-all-btn');
    if (selectAllBtn && !((selectAllBtn as any)._bound)) {
      (selectAllBtn as any)._bound = true;
      selectAllBtn.addEventListener('click', function(this: any) {
        var pageOrders = (SellBaseData && SellBaseData.orders) || [];
        var ids = (state as any).ordersSelectedIds;
        // Czy wszystkie na stronie są już zaznaczone? → toggle off
        var allSelected = pageOrders.length > 0 && pageOrders.every(function(o: OrderRow) { return ids.has(o.id); });
        if (allSelected) {
          // Odznacz całą stronę (zostaje w trybie wyboru — user moze recznie dobrac)
          pageOrders.forEach(function(o: OrderRow) { ids.delete(o.id); });
        } else {
          // Zaznacza WYLACZNIE biezaca strone (decyzja usera 2026-08-10) — nie wszystkie
          // pasujace do filtra. Jeden klik nie moze objac czternastu tysiecy zamowien.
          pageOrders.forEach(function(o: OrderRow) { ids.add(o.id); });
        }
        _updateSelectModeUI();
        _updateBulkBar();
        renderOrdersView();
      });
    }

    // 2026-08-21 (zgłoszenie usera: „gdzie na screenie widzisz zaznacz wszystkie?
    // nie ma go"). Przycisk ma w szablonie style="display:none", a odsłania go
    // WYŁĄCZNIE _updateSelectModeUI — wołane dotąd tylko z procedur kliknięcia,
    // w tym z tego samego przycisku. Jajko i kura: schowany przycisk mógł odsłonić
    // się jedynie po kliknięciu w siebie, więc od 2026-08-10 był niewidoczny.
    // Wywołanie przy wiązaniu paska pokazuje go od razu po wejściu na listę.
    _updateSelectModeUI();
}

export function _updateSelectModeUI() {
    // 2026-08-10: nie ma juz trybu wybierania. Stary przycisk „Wybierz zamowienia"
    // chowamy (zostaje w HTML, zeby nie ruszac szablonu), a „Zaznacz wszystkie" jest
    // widoczne ZAWSZE i przejmuje jego miejsce w pasku.
    var stary = document.getElementById('orders-select-mode-btn');
    if (stary) (stary as HTMLElement).style.display = 'none';

    var selectAllBtn = document.getElementById('orders-select-all-btn');
    if (!selectAllBtn) return;
    (selectAllBtn as HTMLElement).style.display = '';
    var pageOrders = (SellBaseData && SellBaseData.orders) || [];
    var ids = (state as any).ordersSelectedIds;
    // Etykieta mowi, co sie stanie po klikniciu — a nie w jakim jestesmy trybie.
    var allSel = pageOrders.length > 0 && pageOrders.every(function(o: OrderRow) { return ids.has(o.id); });
    (selectAllBtn as any).innerHTML = allSel
      ? '<i data-lucide="square" style="width:14px;height:14px"></i> Odznacz wszystkie'
      : '<i data-lucide="list-checks" style="width:14px;height:14px"></i> Zaznacz wszystkie';
    try { lucide.createIcons({ nodes: [selectAllBtn as HTMLElement] }); } catch (_) {}
}

// ── „Zaznacz wszystkie pasujące do filtra" (zgłoszenie usera 2026-08-21) ──────
// Dotychczasowy przycisk obejmował WYŁĄCZNIE bieżącą stronę (decyzja z 2026-08-10:
// jeden klik nie może objąć czternastu tysięcy zamówień). Teraz obie możliwości są
// dostępne, ale druga jest świadomym, osobnym kliknięciem z widoczną liczbą.
//
// Identyfikatory dobieramy stronami po 200 (twardy limit `per_page` w backendzie),
// tym samym filtrem co lista — parametry ostatniego zapytania są zapamiętane przy
// jego wysyłce. Bez tego trzeba by powielić kilkadziesiąt linii budowania filtrów.
const _LIMIT_ZAZNACZENIA = 5000;

async function _zaznaczWszystkiePasujace(link: HTMLElement): Promise<void> {
  var stanExt: any = (window as any).__sbStateExt || {};
  var bazowe = stanExt._ordersLastParams;
  if (!bazowe) { showToast('Odśwież listę i spróbuj ponownie', 'warning'); return; }

  var ile = Number((state as any).ordersTotalCount || 0);
  if (ile > _LIMIT_ZAZNACZENIA) {
    if (!(await showConfirm('Zaznaczyć pierwsze ' + _LIMIT_ZAZNACZENIA + '?',
        'Filtr obejmuje ' + ile + ' zamówień — dalej trzeba go zawęzić.'))) return;
  }

  var pierwotny = link.textContent;
  link.textContent = 'Zaznaczam…';
  (link as any).style.pointerEvents = 'none';
  var ids = (state as any).ordersSelectedIds;
  var dodane = 0;
  try {
    for (var strona = 1; strona <= Math.ceil(_LIMIT_ZAZNACZENIA / 200); strona++) {
      var params = Object.assign({}, bazowe, { page: strona, per_page: 200 });
      var resp = await SellBaseAPI.getOrdersUnified(params);
      var paczka = (resp && (resp.orders || resp.items)) || [];
      if (!paczka.length) break;
      paczka.forEach(function(o: any) { if (o && o.id) { ids.add(o.id); dodane++; } });
      if (dodane >= _LIMIT_ZAZNACZENIA) break;
      var stron = Number(resp && resp.pages) || 1;
      if (strona >= stron) break;
    }
    showToast('Zaznaczono ' + ids.size + ' zamówień', 'success');
  } catch (e: any) {
    showToast('Nie udało się zaznaczyć wszystkich: ' + String((e && e.message) || e), 'error');
  } finally {
    link.textContent = pierwotny;
    (link as any).style.pointerEvents = '';
  }
  _updateBulkBar();
  renderOrdersView();
}

export function _updateBulkBar() {
    var bar = document.getElementById('orders-bulk-bar');
    var countEl = document.getElementById('bulk-count');
    var n = (state as any).ordersSelectedIds.size;
    if (bar) bar.style.display = n > 0 ? 'flex' : 'none';
    if (countEl) countEl.textContent = n + ' zaznaczone';
    // S-160 (MOB-2): na telefonie pasek siedzi na dole ekranu zwinięty do licznika i „Akcje” (mobile.css);
    // na laptopie przycisk jest schowany (style.css). Po odznaczeniu wszystkiego pasek wraca do zwiniętego.
    if (bar && !document.getElementById('bulk-mobile-toggle')) {
      var tog = document.createElement('button');
      tog.type = 'button';
      tog.id = 'bulk-mobile-toggle';
      tog.className = 'btn-primary btn-sm bulk-mobile-toggle';
      tog.setAttribute('aria-expanded', 'false');
      tog.textContent = 'Akcje';
      tog.addEventListener('click', function() {
        var rozwiniety = bar!.classList.toggle('bulk-rozwiniety');
        tog.setAttribute('aria-expanded', String(rozwiniety));
        tog.textContent = rozwiniety ? 'Zwiń' : 'Akcje';
      });
      if (countEl && countEl.parentNode === bar) bar.insertBefore(tog, countEl.nextSibling);
      else bar.insertBefore(tog, bar.firstChild);
    }
    if (bar && n === 0 && bar.classList.contains('bulk-rozwiniety')) {
      bar.classList.remove('bulk-rozwiniety');
      var t0 = document.getElementById('bulk-mobile-toggle');
      if (t0) { t0.setAttribute('aria-expanded', 'false'); t0.textContent = 'Akcje'; }
    }
    // Gdy filtr obejmuje WIĘCEJ niż zaznaczono — propozycja objęcia całości.
    // Wzorzec znany ze skrzynek pocztowych: zaznaczasz stronę, a system pyta,
    // czy chodziło o wszystkie wyniki.
    try {
      var wszystkich = Number((state as any).ordersTotalCount || 0);
      var pasek = document.getElementById('orders-bulk-bar');
      var link = document.getElementById('orders-select-matching');
      if (pasek && n > 0 && wszystkich > n) {
        if (!link) {
          link = document.createElement('button');
          link.id = 'orders-select-matching';
          link.className = 'btn-ghost btn-sm';
          (link as HTMLElement).style.color = 'var(--color-primary)';
          link.addEventListener('click', function() { _zaznaczWszystkiePasujace(link as HTMLElement); });
          pasek.appendChild(link);
        }
        link.textContent = 'Zaznacz wszystkie pasujące (' + wszystkich + ')';
        (link as HTMLElement).style.display = '';
      } else if (link) {
        (link as HTMLElement).style.display = 'none';
      }
    } catch (_e) {}
    // 2026-07-24: licznik „Połącz (n)" — ile z zaznaczonych da się połączyć (ten sam
    // kupujący + źródło; ostateczna walidacja server-side w preflight).
    try { ((window as any)._updateBulkMergeCount || function () {})(); } catch (_) {}
    // Populate status select
    var sel = document.getElementById('bulk-status-select');
    if (sel && (sel as any).options.length < 2) {
      var sorted = (SellBaseData.orderStatuses || []).slice().sort(function(a: OrderStatusDef, b: OrderStatusDef) { return (a.sort_order || 0) - (b.sort_order || 0); });
      sorted.forEach(function(s: OrderStatusDef) {
        var opt = document.createElement('option');
        opt.value = s.name;
        opt.textContent = s.name;
        sel!.appendChild(opt);
      });
    }
}

// ── EXPOSE ──
(window as any).SellBaseApp = (window as any).SellBaseApp || {};
(window as any).renderOrdersView = renderOrdersView; (window as any).SellBaseApp.renderOrdersView = renderOrdersView;
(window as any)._bindOrdersToolbarEvents = _bindOrdersToolbarEvents; (window as any).SellBaseApp._bindOrdersToolbarEvents = _bindOrdersToolbarEvents;
(window as any)._updateSelectModeUI = _updateSelectModeUI; (window as any).SellBaseApp._updateSelectModeUI = _updateSelectModeUI;
(window as any)._updateBulkBar = _updateBulkBar; (window as any).SellBaseApp._updateBulkBar = _updateBulkBar;
